/**
 * Utility functions for evaluating delivery status of POs and Shipments across Logistics, Inventory, and Financials.
 */

/**
 * Checks if a specific item or vendor split key is delivered in a shipment.
 */
export function isItemDelivered(shipment, itemKey) {
  if (!shipment) return false;
  if (shipment.status === "Delivered") return true;
  const stat = shipment.itemStatuses?.[itemKey];
  if (typeof stat === "object") return stat?.status === "Delivered";
  if (typeof stat === "string") return stat === "Delivered";
  return false;
}

/**
 * Helper to build standard shipping rows for a PO matching Logistics table structure.
 */
export function getPoShippingRows(poItems = []) {
  const shippingRows = [];
  poItems.forEach(item => {
    if (item.vendors && item.vendors.length > 0) {
      item.vendors.forEach(v => {
        shippingRows.push({
          ...item,
          vendorKey: `${item.id}_${v.id}`,
          vendorName: v.vendor,
          vendorQty: v.quantity,
          vendorCost: v.vendorPrice,
          vendorUrl: v.url,
          originalItemId: item.id,
          vendorId: v.id,
          isSplit: true
        });
      });
    } else {
      shippingRows.push({
        ...item,
        vendorKey: item.id,
        vendorName: item.vendor || "—",
        vendorQty: item.quantity || 0,
        vendorCost: item.vendorPrice || 0,
        vendorUrl: item.url || "",
        originalItemId: item.id,
        vendorId: "default",
        isSplit: false
      });
    }
  });
  return shippingRows;
}

/**
 * Checks if all items belonging to a PO are fully delivered across its linked shipment(s).
 */
export function isPoDelivered({ po, shipment = null, shipments = [] }) {
  if (!po) return false;

  // Find all matching shipment records for this PO
  const matchingShipments = shipments.filter(s =>
    (po.id && s.poId === po.id) ||
    (po.poNumber && s.poNumber === po.poNumber) ||
    (shipment && s.id === shipment.id)
  );

  const targetShipment = shipment || matchingShipments[0] || null;

  // 1. If PO has explicit deliveredDate or top-level status Delivered
  if (po.deliveredDate || po.status === "Delivered") {
    return true;
  }

  // 2. If top-level shipment status is Delivered
  if (targetShipment?.status === "Delivered" || (matchingShipments.length > 0 && matchingShipments.every(s => s.status === "Delivered"))) {
    return true;
  }

  if (!targetShipment && matchingShipments.length === 0) {
    return false;
  }

  const poItems = po.items || [];
  if (!poItems.length) {
    return (targetShipment?.status || "To Order") === "Delivered";
  }

  const shippingRows = getPoShippingRows(poItems);
  if (!shippingRows.length) {
    return (targetShipment?.status || "To Order") === "Delivered";
  }

  // 3. Verify that every shipping row is Delivered across matching shipments
  return shippingRows.every(row => {
    for (const s of matchingShipments) {
      if (s.status === "Delivered") return true;

      const statVal = s.itemStatuses?.[row.vendorKey] ??
                      s.itemStatuses?.[row.id] ??
                      (row.id ? Object.entries(s.itemStatuses || {}).find(([k]) =>
                        k === row.vendorKey || k === row.id || k.startsWith(row.id + "_")
                      )?.[1] : null);

      const itemStatus = (typeof statVal === "object" ? statVal?.status : statVal) || s.status || "To Order";
      if (itemStatus === "Delivered") return true;
    }
    return false;
  });
}

/**
 * Checks if a specific shipment document has all of its line items delivered.
 */
export function isShipmentDelivered(shipment, pos = []) {
  if (!shipment) return false;
  if (shipment.status === "Delivered") return true;

  const po = pos.find(p => (shipment.poId && p.id === shipment.poId) || (shipment.poNumber && p.poNumber === shipment.poNumber));
  const poItems = po?.items || [];

  if (!poItems.length) {
    return (shipment.status || "To Order") === "Delivered";
  }

  const shippingRows = getPoShippingRows(poItems);
  if (!shippingRows.length) {
    return (shipment.status || "To Order") === "Delivered";
  }

  return shippingRows.every(row => {
    const statVal = shipment.itemStatuses?.[row.vendorKey] ??
                    shipment.itemStatuses?.[row.id] ??
                    (row.id ? Object.entries(shipment.itemStatuses || {}).find(([k]) =>
                      k === row.vendorKey || k === row.id || k.startsWith(row.id + "_")
                    )?.[1] : null);

    const itemStatus = (typeof statVal === "object" ? statVal?.status : statVal) || shipment.status || "To Order";
    return itemStatus === "Delivered";
  });
}
